/** Import: choose where the pages go and the files for one document, upload, read the server's check, then import. */
import { DOCKET_HINT, DOCKET_PATTERN, type EstateSearchResponse, type ImportInfo, type ImportReport } from '@imagesync/core';
import { ApiError, get, post, upload } from './api.js';
import { hasModule, office } from './state.js';
import { $, $maybe, esc, kb, num, openDialog, closeDialog, plural, setErr, toast } from './util.js';

type Kind = 'book' | 'estate' | 'index';
const I = {
  kind: 'book' as Kind, files: [] as File[], phase: 'setup' as 'setup' | 'uploading' | 'checking' | 'checked' | 'importing' | 'done' | 'failed',
  info: null as (ImportInfo & { files?: { name: string; bytes: number }[] }) | null, abort: null as null | (() => void), timer: undefined as ReturnType<typeof setTimeout> | undefined,
  target: null as Record<string, unknown> | null, message: '',
};
let hooks = { openBookPage: (_t: string, _b: string, _p: string) => undefined as void };
const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');

export function openImport(): void {
  if (!['uploading', 'checking', 'importing'].includes(I.phase)) reset();
  render();
  openDialog($<HTMLDialogElement>('#importDlg'));
}
function reset(): void {
  clearTimeout(I.timer);
  I.phase = 'setup'; I.files = []; I.info = null; I.target = null; I.message = '';
  I.kind = office().bookTypes.length && hasModule('bookpage') ? 'book' : hasModule('estates') ? 'estate' : 'index';
}

function kinds(): [Kind, string][] {
  const out: [Kind, string][] = [];
  if (office().bookTypes.length && hasModule('bookpage')) out.push(['book', 'A book']);
  if (hasModule('estates')) out.push(['estate', 'An estate case file']);
  if (office().indexes.length && hasModule('index')) out.push(['index', 'An index book']);
  return out;
}

function targetHTML(): string {
  const o = office();
  if (I.kind === 'book') {
    const bt = o.bookTypes[0];
    const [r1, r2] = /mortgage/i.test(bt?.label ?? '') ? ['Mortgagor', 'Mortgagee'] : /will/i.test(bt?.label ?? '') ? ['Testator', ''] : ['Grantor', 'Grantee'];
    return '<div class="row2"><label class="field" for="imType"><span class="label">Book type</span><select id="imType">' + o.bookTypes.map(b => '<option value="' + esc(b.id) + '">' + esc(b.plural) + '</option>').join('') + '</select></label>' +
      '<label class="field" for="imKind"><span class="label">Document</span><input type="text" id="imKind" value="' + esc(bt?.short ?? '') + '" placeholder="Deed"></label></div>' +
      '<div class="row2"><label class="field" for="imBook"><span class="label">Book</span><input type="text" id="imBook" class="mono" inputmode="numeric" autocomplete="off"></label>' +
      '<label class="field" for="imFirst"><span class="label">First page</span><input type="text" id="imFirst" class="mono" inputmode="numeric" autocomplete="off" value="1"></label></div>' +
      '<div class="row2"><label class="field" for="imRecorded"><span class="label">Recorded</span><input type="date" id="imRecorded"></label>' +
      '<label class="field" for="imInstr"><span class="label">Instrument no.</span><input type="text" id="imInstr" class="mono" autocomplete="off" placeholder="Optional"></label></div>' +
      '<div class="row2"><label class="field" for="imR1"><span class="label">Party</span><input type="text" id="imR1" value="' + esc(r1) + '"></label><label class="field" for="imN1"><span class="label">Names</span><input type="text" id="imN1" autocomplete="off"></label></div>' +
      '<div class="row2"><label class="field" for="imR2"><span class="label">Party</span><input type="text" id="imR2" value="' + esc(r2) + '"></label><label class="field" for="imN2"><span class="label">Names</span><input type="text" id="imN2" autocomplete="off"></label></div>';
  }
  if (I.kind === 'estate') return '<label class="field" for="imDocket" style="max-width:260px"><span class="label">Docket no.</span><input type="text" id="imDocket" class="mono" placeholder="1986-0233" autocomplete="off"></label><p class="hint">The pages are added to the end of that record’s case file.</p>';
  const ix = o.indexes[0]!;
  return '<div class="row2"><label class="field" for="imIndex"><span class="label">Index</span><select id="imIndex">' + o.indexes.map(i => '<option value="' + esc(i.id) + '">' + esc(i.label) + '</option>').join('') + '</select></label>' +
    '<label class="field" for="imSeries"><span class="label">Series</span><select id="imSeries">' + ix.series.map(s => '<option value="' + esc(s.id) + '">' + esc(s.label) + '</option>').join('') + '</select></label></div>' +
    '<div class="row2"><label class="field" for="imLetter"><span class="label">Book (letter)</span><input type="text" id="imLetter" class="mono" maxlength="1" autocomplete="off" placeholder="N"></label>' +
    '<label class="field" for="imSection"><span class="label">Section</span><input type="text" id="imSection" class="mono" inputmode="numeric" autocomplete="off" placeholder="0 is the guide"></label></div>' +
    '<p class="hint">Several files fill consecutive sections, starting at this one.</p>';
}

function reportHTML(r: ImportReport): string {
  const notes = r.notes.map(([lvl, t]) => '<li class="' + lvl + '">' + esc(t) + '</li>').join('');
  const files = (r.files ?? []).map(f => '<li class="ck-file"><span class="ck-ph" aria-hidden="true"></span><span class="ck-main"><span class="ck-name">' + esc(f.name) + '</span>' +
    (f.error ? '<span class="ck-err">' + esc(f.error) + '</span>' : '<span class="ck-meta">' + esc(f.label ?? '') + ' ' + '· ' + num(f.count ?? 0) + ' ' + plural(f.count ?? 0, 'page') + ' · ' + kb(f.size) + '</span>' +
      (f.pages?.[0] ? '<span class="ck-sub">' + esc(f.pages[0].enc) + '</span>' : '')) + '</span><span></span></li>').join('');
  return '<p><b>' + esc(r.summary) + '</b></p>' + (notes ? '<ul class="ck-notes">' + notes + '</ul>' : '') + (files ? '<ul class="ck-list">' + files + '</ul>' : '');
}

function render(): void {
  const body = $('#importBody'), foot = $('#importFoot');
  if (I.phase === 'setup') {
    const ks = kinds();
    body.innerHTML = (ks.length > 1 ? '<div class="field"><span class="label">Where the pages go</span><div class="seg" role="group" id="imKinds">' + ks.map(([k, l]) => '<button type="button" data-kind="' + k + '" aria-pressed="' + (k === I.kind) + '">' + esc(l) + '</button>').join('') + '</div></div>' : '') +
      '<div class="imp-target" id="imTarget">' + targetHTML() + '</div>' +
      '<label class="drop" id="imDrop" for="imFiles"><input type="file" id="imFiles" multiple accept=".tif,.tiff,.pdf,.jpg,.jpeg,.png,image/tiff,application/pdf,image/jpeg,image/png">' +
      '<span class="drop-main">' + (I.files.length ? esc(I.files.length + ' ' + plural(I.files.length, 'file') + ' chosen: ' + I.files.slice(0, 4).map(f => f.name).join(', ') + (I.files.length > 4 ? '…' : '')) : 'Choose files, or drop them here') + '</span>' +
      '<span class="drop-sub">One multi-page TIFF or PDF, or single-page files (one per page) in name order. JPEG and PNG work too.</span></label>' +
      '<p class="err" id="imErr" hidden></p>';
    foot.innerHTML = '<button type="button" class="btn primary" id="imUpload"' + (I.files.length ? '' : ' disabled') + '>Upload and check</button>';
    return;
  }
  if (I.phase === 'uploading') {
    body.innerHTML = '<div class="imp-status">Uploading ' + esc(I.files.length + ' ' + plural(I.files.length, 'file')) + '…</div><div class="progress"><span id="imProg"></span></div>';
    foot.innerHTML = '<button type="button" class="btn" id="imCancelUpload">Cancel</button>';
    return;
  }
  if (I.phase === 'checking') {
    body.innerHTML = '<div class="imp-status" role="status">Checking the files on the server…</div>';
    foot.innerHTML = '<button type="button" class="btn" id="imCancel">Cancel import</button>';
    return;
  }
  if (I.phase === 'checked' && I.info?.report) {
    const r = I.info.report, errs = r.notes.some(n => n[0] === 'err');
    body.innerHTML = reportHTML(r) + (r.ready ? '' : errs ? '<p class="err">Fix the files that can’t be read, then upload them again.</p>' : '<p class="hint">Look over the notes above. You can import anyway.</p>');
    foot.innerHTML = (errs ? '' : '<button type="button" class="btn primary" id="imCommit">' + (r.ready ? 'Import' : 'Import anyway') + ' ' + num(r.pages) + ' ' + plural(r.pages, 'page') + '</button>') +
      '<button type="button" class="btn" id="imCancel">Cancel import</button>';
    return;
  }
  if (I.phase === 'importing') {
    body.innerHTML = '<div class="imp-status" role="status">Importing, then preparing the pages for viewing…</div>' + (I.info?.report ? reportHTML(I.info.report) : '');
    foot.innerHTML = '';
    return;
  }
  if (I.phase === 'done') {
    const n = I.info?.pages ?? 0;
    body.innerHTML = '<p class="okmsg">Imported ' + num(n) + ' ' + plural(n, 'page') + '. ' + (I.message || 'They’re being prepared for viewing now; each takes a few seconds.') + '</p>';
    foot.innerHTML = (I.target?.kind === 'book' ? '<button type="button" class="btn primary" id="imOpen">Open the pages</button>' : '') + '<button type="button" class="btn" id="imAgain">Import another document</button>';
    return;
  }
  body.innerHTML = '<p class="err">' + esc(I.message || 'The import didn’t finish.') + '</p>';
  foot.innerHTML = '<button type="button" class="btn" id="imAgain">Start again</button>';
}

async function readTarget(): Promise<Record<string, unknown> | string> {
  const v = (id: string) => ($maybe<HTMLInputElement>('#' + id)?.value ?? '').trim();
  if (I.kind === 'book') {
    if (!/^([A-Za-z]{1,2})?\d{1,4}$/.test(v('imBook'))) return 'Enter the book number, up to four digits.';
    if (!/^\d{1,4}[A-Za-z]?$/.test(v('imFirst'))) return 'Enter the first page number.';
    if (!v('imKind')) return 'Enter the kind of document, such as Deed.';
    const parties = [[v('imR1'), v('imN1')], [v('imR2'), v('imN2')]].filter(([r, n]) => r && n).map(([role, names]) => ({ role, names }));
    return { kind: 'book', bookType: v('imType'), book: v('imBook'), firstPage: v('imFirst'), docKind: v('imKind'), recorded: v('imRecorded') || null, parties, ...(v('imInstr') ? { instrument: v('imInstr') } : {}) };
  }
  if (I.kind === 'estate') {
    const d = v('imDocket');
    if (!DOCKET_PATTERN.test(d)) return DOCKET_HINT;
    const r = await get<EstateSearchResponse>('/api/v1/estates?docket=' + encodeURIComponent(d) + '&size=25');
    const rec = r.rows.find(x => x.docket === d);
    if (!rec) return 'There’s no estate record with docket ' + d + '.';
    return { kind: 'estate', estateId: rec.id };
  }
  if (!/^[A-Za-z]$/.test(v('imLetter'))) return 'Enter the book’s letter, A to Z.';
  if (!/^\d{1,5}$/.test(v('imSection'))) return 'Enter the section number (0 for the guide).';
  return { kind: 'index', indexId: v('imIndex'), series: v('imSeries'), book: v('imLetter').toUpperCase(), section: Number(v('imSection')) };
}

async function startUpload(): Promise<void> {
  const err = $('#imErr');
  let target: Record<string, unknown> | string;
  try { target = await readTarget(); } catch (e) { setErr(err, errMsg(e)); return; }
  if (typeof target === 'string') { setErr(err, target); return; }
  if (!I.files.length) { setErr(err, 'Choose the files for the document.'); return; }
  I.target = target;
  const fd = new FormData();
  fd.append('target', JSON.stringify(target));
  for (const f of I.files) fd.append('files', f, f.name);
  I.phase = 'uploading'; render();
  const up = upload<ImportInfo>('/api/v1/imports', fd, frac => { const p = $maybe('#imProg'); if (p) p.style.width = Math.round(frac * 100) + '%'; });
  I.abort = up.abort;
  try {
    I.info = await up.done;
    I.phase = 'checking'; render();
    poll();
  } catch (e) {
    I.phase = 'setup'; render();
    if (!(e instanceof ApiError && e.code === 'aborted')) setErr($('#imErr'), errMsg(e));
  } finally { I.abort = null; }
}
function poll(): void {
  clearTimeout(I.timer);
  I.timer = setTimeout(async () => {
    if (!I.info) return;
    try {
      I.info = await get<ImportInfo>('/api/v1/imports/' + I.info.id);
      if (I.info.status === 'checked') I.phase = 'checked';
      else if (I.info.status === 'done') { I.phase = 'done'; }
      else if (I.info.status === 'failed') { I.phase = 'failed'; I.message = I.info.message ?? ''; }
      render();
      if (I.phase === 'checking' || I.phase === 'importing') poll();
      else if (I.phase === 'done') { if (!$<HTMLDialogElement>('#importDlg').open) toast('Import finished: ' + num(I.info.pages) + ' ' + plural(I.info.pages, 'page') + '.'); }
    } catch { poll(); }
  }, 900);
}
async function commit(): Promise<void> {
  if (!I.info?.report) return;
  try {
    I.info = await post<ImportInfo>(`/api/v1/imports/${I.info.id}/commit`, { acceptWarnings: !I.info.report.ready });
    I.phase = 'importing'; render(); poll();
  } catch (e) { toast(errMsg(e), 'err'); }
}
async function cancel(): Promise<void> {
  if (I.info && ['checking', 'checked'].includes(I.phase)) { try { await post(`/api/v1/imports/${I.info.id}/cancel`); } catch { /* already gone */ } }
  reset(); render();
}

export function wireImport(h: typeof hooks): void {
  hooks = h;
  const dlg = $<HTMLDialogElement>('#importDlg');
  $('#importClose').addEventListener('click', () => closeDialog(dlg));
  dlg.addEventListener('click', e => {
    const t = e.target as HTMLElement;
    const k = t.closest<HTMLElement>('[data-kind]');
    if (k) { I.kind = k.dataset.kind as Kind; render(); return; }
    if (t.id === 'imUpload') void startUpload();
    else if (t.id === 'imCancelUpload') I.abort?.();
    else if (t.id === 'imCommit') void commit();
    else if (t.id === 'imCancel') void cancel();
    else if (t.id === 'imAgain') { reset(); render(); }
    else if (t.id === 'imOpen' && I.target?.kind === 'book') { closeDialog(dlg); hooks.openBookPage(String(I.target.bookType), String(I.target.book), String(I.target.firstPage)); }
  });
  dlg.addEventListener('change', e => {
    const t = e.target as HTMLInputElement;
    if (t.id === 'imFiles' && t.files) { I.files = Array.from(t.files); const keep = snapshot(); render(); restore(keep); }
    if (t.id === 'imIndex') {
      const ix = office().indexes.find(i => i.id === t.value);
      const sel = $maybe('#imSeries');
      if (ix && sel) sel.innerHTML = ix.series.map(s => '<option value="' + esc(s.id) + '">' + esc(s.label) + '</option>').join('');
    }
  });
  dlg.addEventListener('dragover', e => { if ((e.target as HTMLElement).closest('#imDrop')) { e.preventDefault(); $('#imDrop').classList.add('over'); } });
  dlg.addEventListener('dragleave', e => { if ((e.target as HTMLElement).closest('#imDrop')) $('#imDrop').classList.remove('over'); });
  dlg.addEventListener('drop', e => {
    if (!(e.target as HTMLElement).closest('#imDrop')) return;
    e.preventDefault();
    if (e.dataTransfer?.files.length) { I.files = Array.from(e.dataTransfer.files); const keep = snapshot(); render(); restore(keep); }
  });
}
/** Keep what's typed in the destination fields across a re-render. */
function snapshot(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const el of Array.from(document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('#imTarget input, #imTarget select'))) out[el.id] = el.value;
  return out;
}
function restore(v: Record<string, string>): void {
  for (const [id, val] of Object.entries(v)) { const el = document.getElementById(id) as HTMLInputElement | null; if (el) el.value = val; }
}
